
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "new-entity-transforms-framework"
    }}>{`(New) Entity Transforms Framework`}</h1>
    <h4 {...{
      "id": "background"
    }}>{`Background`}</h4>
    <p>{`During Import Process, entity transforms are required to make changes to the entity before it gets committed to the database. These modifications are necessary to make the entity conform to the environment it is going to reside. The Import Process provided a mechanism to do that.`}</p>
    <h4 {...{
      "id": "transformation-framework"
    }}>{`Transformation Framework`}</h4>
    <p>{`A transformation framework allows a mechanism to selectively transform an entity or specific attributes of that entity.`}</p>
    <p>{`To achieve this, the framework provides:`}</p>
    <ul>
      <li parentName="ul">{`Way to set a condition that needs to be satisfied for a transformation to be applied.`}</li>
      <li parentName="ul">{`Action to be taken on the entity once the condition is met.`}</li>
    </ul>
    <p>{`The existing transformation frameworks allowed this to happen.`}</p>
    <h4 {...{
      "id": "reason-for-new-transformation-framework"
    }}>{`Reason for New Transformation Framework`}</h4>
    <p>{`While the existing framework provided the basic benefits of the transformation framework, it did not have support for some commonly used Atlas types. Which meant that users of this framework would have to meticulously define transformations for every type they are working with. This can be tedious and potentially error-prone.
The new framework addresses this problem by providing built-in transformations for some commonly used types. It can also be extended to accommodate new types.`}</p>
    <h4 {...{
      "id": "approach"
    }}>{`Approach`}</h4>
    <p>{`The approach used by the new transformation framework creates a transformation by:`}</p>
    <ul>
      <li parentName="ul">{`Specifying a condition.`}</li>
      <li parentName="ul">{`Specifying action(s) to be taken if the condition is met.`}</li>
    </ul>
    <h5 {...{
      "id": "conditions"
    }}>{`Conditions`}</h5>
    <p>{`Following are built-in conditions.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Condition Types`}</strong></th>
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Description`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ENTITY_ALL`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Any/every entity`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ENTITY_TOP_LEVEL`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity that is the top-level entity. This is also the entity present specified in `}<em parentName="td">{`AtlasExportRequest`}</em>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`EQUALS`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity attribute equals to the one specified in the condition.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`EQUALS_IGNORE_CASE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity attribute equals to the one specified in the condition ignoring case.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`STARTS_WITH`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity attribute starts with.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`STARTS_WITH_IGNORE_CASE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity attribute starts with ignoring case.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`HAS_VALUE`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entity attribute has value.`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "actions"
    }}>{`Actions`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}><strong parentName="th">{`Action Type`}</strong></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`*Description**`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ADD_CLASSIFICATION`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Add classification`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`REPLACE_PREFIX`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Replace value starting with another value.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`TO_LOWER`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Convert a value of an attribute to lower case.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`SET`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Set the value of an attribute`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`CLEAR`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Clear value of an attribute`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "built-in-transforms"
    }}>{`Built-in Transforms`}</h4>
    <h5 {...{
      "id": "add-classification"
    }}>{`Add Classification`}</h5>
    <p>{`During import, hive`}<em parentName="p">{`db entity whose _qualifiedName`}</em>{` is `}<em parentName="p">{`stocks@cl1`}</em>{` will get the classification `}<em parentName="p">{`clSrcImported`}</em>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "hive_db.qualifiedName": "stocks@cl1"
    },
    "action": {
        "__entity": "ADD_CLASSIFICATION: clSrcImported"
    }
}`}
    </SyntaxHighlighter>
    <p>{`Every imported entity will get the classification by simply changing the condition. The __entity is a special condition which matches entity.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "__entity": ""
    },
    "action": {
        "__entity": "ADD_CLASSIFICATION: clSrcImported"
    }
}`}
    </SyntaxHighlighter>
    <p>{`To add the classification to only the top-level entity (an entity that is used as a starting point for an export), use:`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "__entity": "topLevel:"
    },
    "action": {
        "__entity": "ADD_CLASSIFICATION: clSrcImported"
    }
}`}
    </SyntaxHighlighter>
    <h5 {...{
      "id": "replace-prefix"
    }}>{`Replace Prefix`}</h5>
    <p>{`This action works on string values. The first parameter is the prefix that is searched for a match, once matched, it is replaced with the provided replacement string.`}</p>
    <p>{`The sample below searches for `}<em parentName="p">{`/aa/bb/`}</em>{`, once found replaces it with `}<em parentName="p">{`/xx/yy/`}</em>{`.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "hdfs_path.clusterName": "EQUALS: CL1"
    },
    "action": {
        "hdfs_path.path": "REPLACE_PREFIX: = :/aa/bb/=/xx/yy/"
    }
}`}
    </SyntaxHighlighter>
    <h5 {...{
      "id": "to-lower"
    }}>{`To Lower`}</h5>
    <p>{`Entity whose hdfs_path.clusterName is CL1 will get its path attribute converted to lower case.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "hdfs_path.clusterName": "EQUALS: CL1"
    },
    "action": {
        "hdfs_path.path": "TO_LOWER:"
    }
}`}
    </SyntaxHighlighter>
    <h5 {...{
      "id": "clear"
    }}>{`Clear`}</h5>
    <p>{`Entity whose hdfs`}<em parentName="p">{`path.clusterName has value set, will get its _replicatedTo`}</em>{` attribute value cleared.`}</p>
    <SyntaxHighlighter wrapLines={true} language="json" style={theme.dark} mdxType="SyntaxHighlighter">
      {`{
    "conditions": {
        "hdfs_path.clusterName": "HAS_VALUE:"
    },
    "action": {
        "hdfs_path.replicatedTo": "CLEAR:"
    }
}`}
    </SyntaxHighlighter>
    <h4 {...{
      "id": "additional-examples"
    }}>{`Additional Examples`}</h4>
    <p>{`Please look at `}<a parentName="p" {...{
        "href": "https://github.com/apache/atlas/blob/master/intg/src/test/java/org/apache/atlas/entitytransform/TransformationHandlerTest.java"
      }}>{`these tests`}</a>{` for examples using Java classes.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;